import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Divider, Dropdown } from '@ets/design-system';
import { SplitToggle } from '_common/components';

import { useSelector, useDispatch } from '_common/hooks';
import EditorManager from 'Editor/services/EditorManager';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import { selectReadOnlyMode } from 'Editor/redux/EditorStatusSlice';
import { getOutlineList } from 'Editor/redux/listStylesSlice';
import { selectedMultilevelList } from 'Editor/redux/ToolbarSlice';
import PreviewLists from 'Editor/components/PreviewLists/PreviewLists';

import styles from './OutlineList.module.scss';

const OutlineList = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const selected = useSelector((state) => selectedMultilevelList(state));
  const outlineStyles = useSelector((state) => getOutlineList(state));
  const selectedList = useSelector((state) => state.editor.toolbar.selectedList);
  const isReadOnlyMode = useSelector((state) => selectReadOnlyMode(state));
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = (element: string) => {
    const manager = EditorManager.getInstance();
    if (element === selectedList) {
      manager.toggleList(false);
    } else {
      manager.toggleList(element);
    }
  };

  const handleListAction = (customize: boolean) =>
    dispatch(
      openAndUpdateModal({
        modal: 'NewListModal',
        data: {
          customize,
          type: 'outline_numbered',
        },
      }),
    );

  return (
    <Dropdown.Root
      open={isOpen}
      onOpenChange={() => {
        if (!isReadOnlyMode) setIsOpen(!isOpen);
      }}
    >
      <Dropdown.Trigger>
        <SplitToggle
          isToggled={selected || isOpen}
          icon="list-tree"
          iconColor="neutral"
          isOpen={selected || isOpen}
          disabled={isReadOnlyMode}
          testId="outline-list"
          margin="2px"
          onClick={() => {
            handleSelect(selectedList);
          }}
          onPopperClick={() => setIsOpen(!isOpen)}
        />
      </Dropdown.Trigger>

      <Dropdown.Content
        align="end"
        style={{
          width: Object.keys(outlineStyles).length > 5 ? '32.5rem' : '30rem',
          paddingTop: 0,
        }}
        testId="outline-list-dropdown"
      >
        <div className={styles.styles}>
          <div
            className={`${styles.item} ${styles.none} ${!selected && styles.selected}`}
            onClick={() => {
              handleSelect('');
              setIsOpen(false);
            }}
            data-testid="none"
          >
            {intl.formatMessage({ id: 'NONE' }).toUpperCase()}
          </div>
          {Object.keys(outlineStyles).map((element) => (
            <div
              key={element}
              onClick={() => {
                handleSelect(element);
                setIsOpen(false);
              }}
              className={`${styles.item} ${element === selectedList && styles.selected}`}
              data-testid={`${element}-list`}
            >
              <PreviewLists list={outlineStyles[element]} />
            </div>
          ))}
        </div>
        <Divider margin="0.25rem 0 0.25rem 0" />
        {Object.keys(outlineStyles).length > 0 && (
          <Dropdown.Item
            onClick={() => handleListAction(true)}
            testId="outline-list-customize-list-styles"
          >
            <FormattedMessage id="CUSTOMIZE_LIST_STYLES" />
          </Dropdown.Item>
        )}
        <Dropdown.Item
          onClick={() => handleListAction(false)}
          testId="outline-list-create-new-list"
        >
          <FormattedMessage id="CREATE_NEW_LIST" />
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown.Root>
  );
};

export default OutlineList;
